//==============================================================================
// Bulk Order
//
// Displays a selected category's product list and allows products to be
// added to the cart.
//==============================================================================
import * as React from 'react';
import { observable, action, reaction, when, computed } from 'mobx';
import { observer } from 'mobx-react';
import classnames from 'classnames';

import MsDyn365, { getCatalogId, Image, RichTextComponent } from '@msdyn365-commerce/core';
import { Modal, ModalBody, ModalHeader, format, Waiting } from '@msdyn365-commerce-modules/utilities';
import { getFallbackImageUrl, QueryResultSettingsProxy } from '@msdyn365-commerce-modules/retail-actions';
import { addCartLinesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import { getActivePricesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { ProjectionDomain } from '@msdyn365-commerce/retail-proxy';

import { clsHelper } from '../../utilities/class-name-helper';
import { FullProductWithAttributes } from '../../actions/get-products-with-attributes-by-category';

import { IBulkOrderData } from './bulk-order.data';
import { IBulkOrderProps } from './bulk-order.props.autogenerated';
import { attrNames } from '../../utilities/global-constants';

//==============================================================================
// CLASS NAME UTILITY
//==============================================================================
const BASE_CLASS = 'bulk-order';
const cls = (fragment?: string) => clsHelper(BASE_CLASS, fragment);

//==============================================================================
// INTERFACES
//==============================================================================
interface Order {
    [itemID: string]: number;
}

interface ProductAddRequest {
    recId: number;
    quantity: number;
}

interface Property {
    label: string;
    src: string;
    cls: string;
    callback?: Function;
}

//==============================================================================
// CLASS DEFINITION
//==============================================================================
/**
 * BulkOrder component
 * @extends {React.Component<IBulkOrderProps<IBulkOrderData>>}
 */
//==============================================================================
@observer
class BulkOrder extends React.Component<IBulkOrderProps<IBulkOrderData>> {

    //==========================================================================
    // PUBLIC METHODS
    //==========================================================================
    @observable private order: Order = {};
    @observable private enableAddToCart: boolean = false;
    @observable private isModalOpen: boolean = false;
    @observable private addToCartError: string = '';
    @observable private hasChanges: boolean = false;

    @computed get isDataReady(): boolean {
        return (this.props.data.products.result && this.props.data.products.status) === 'SUCCESS';
    }

    //------------------------------------------------------
    //------------------------------------------------------
    constructor(props: IBulkOrderProps<IBulkOrderData>) {
        super(props);

        this.quantitySet = this.quantitySet.bind(this);
        this.addToCart = this.addToCart.bind(this);
        this.toggleModal = this.toggleModal.bind(this);

        // filter out unbuyable products
        const buyableProducts = this.props.data.products.result?.filter(item => {
            const buyable = !item.ProductAttributes?.find(atr => {
                return atr.Name === attrNames.notBuyable;
            })?.TextValue;

            return buyable;
        });

        this.props.data.products.result = buyableProducts;

    }

    //------------------------------------------------------
    //------------------------------------------------------
    public async componentDidMount(): Promise<void> {

        when(
            () => this.isDataReady,
            async () => {
                const validInputs = this.props.data.products.result?.map(input => {
                    return input.ProductDetails.RecordId;
                }) || [];

                const projectDomain: ProjectionDomain = { ChannelId: +this.props.context.request.apiSettings.channelId, CatalogId: getCatalogId(this.props.context.request) };

                const prices = await getActivePricesAsync(
                    { callerContext: this.props.context.actionContext, queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(this.props.context.actionContext) },
                    projectDomain,
                    validInputs,
                    new Date(),
                    this.props.context.request.user.customerAccountNumber,
                    undefined,
                    true
                );

                this.props.data.products.result?.forEach(product => {
                    product.ProductDetails.AdjustedPrice = prices.find(price => {
                        return price.ItemId === product.ProductDetails.ItemId;
                    })?.CustomerContextualPrice || product.ProductDetails.AdjustedPrice;
                });
            }
        );

        reaction(
            () => this.hasChanges,
            () => {
                if (MsDyn365.isBrowser) {
                    if (this.hasChanges) {
                        window.addEventListener('beforeunload', this.changesSaved);
                    } else {
                        window.removeEventListener('beforeunload', this.changesSaved);
                    }
                }
            }
        );
    }

    //------------------------------------------------------
    //------------------------------------------------------
    public componentWillUnmount(): void {
        if (MsDyn365.isBrowser) {
            window.removeEventListener('beforeunload', this.changesSaved);
        }
    }

    //------------------------------------------------------
    //------------------------------------------------------
    public render(): JSX.Element {
        const { config, data, resources } = this.props;
        const products = data.products.result || [];
        const hasProducts = !!(products?.length);

        return (
            <>
                <div className={classnames(BASE_CLASS, config.className)}>
                    {!this.isDataReady &&
                        <Waiting className='msc-waiting-circular msc-waiting-lg' />
                    }

                    {/* No results message, if no results found */}
                    {!hasProducts && this.isDataReady &&
                        <RichTextComponent className={cls('empty')} text={config.noResultsText || resources.BulkOrder_noResults} />
                    }

                    {/* Product and attribute table, if results found */}
                    {hasProducts &&
                        <>
                            {/* Show the product List */}
                            {this.productList(products)}

                            {/* Anchor element for sticky bar */}
                            <div className={classnames(cls('anchor'), config.className ? clsHelper(config.className, 'anchor'): '')}/>

                            {/* Add to cart button */}
                            <div className={cls('add-to-cart')}>
                                <div className={cls('add-to-cart-container')}>
                                    <div className={classnames(cls('add-to-cart-items'), 'unique')}>
                                        <span className={cls('add-to-cart-label')}>{resources.BulkOrder_labelUniqueItems}</span>
                                        <span className={cls('add-to-cart-value')}>{this.getUniqueItems()}</span>
                                    </div>
                                    <div className={classnames(cls('add-to-cart-items'), 'total')}>
                                        <span className={cls('add-to-cart-label')}>{resources.BulkOrder_labelTotalItems}</span>
                                        <span className={cls('add-to-cart-value')}>{this.getTotalItems()}</span>
                                    </div>

                                    {this.addToCartError && <div className={cls('add-to-cart-error')}>{this.addToCartError}</div>}

                                    <button
                                        disabled={this.getUniqueItems() <= 0 || !this.enableAddToCart}
                                        className={classnames(cls('add-to-cart-button'))}
                                        aria-label={resources.BulkOrder_addToCartButton}
                                        title={resources.BulkOrder_addToCartButton}
                                        onClick={this.addToCart}
                                    >
                                        {resources.BulkOrder_addToCartButton}
                                    </button>
                                </div>
                            </div>
                        </>
                    }
                </div>
                {this.renderModal()}
            </>
        );
    }

    //------------------------------------------------------
    //------------------------------------------------------
    private renderModal() {
        const totalItems = this.getTotalItems();
        return (
            <Modal isOpen={this.isModalOpen} toggle={this.toggleModal} className={cls('modal')}>
                <ModalHeader toggle={this.toggleModal} className={cls('modal-header')}>
                    {this.props.config.modalHeader}
                </ModalHeader>
                <ModalBody className={cls('modal-body')}>
                    <div className={cls('modal-content')}>
                        {format(
                            this.props.resources.BulkOrder_addedLine,
                            totalItems
                        )}
                    </div>
                </ModalBody>
            </Modal>

        );
    }

    //==========================================================================
    // PRIVATE RENDER METHODS
    //==========================================================================

    //------------------------------------------------------
    // Render the product list
    //------------------------------------------------------
    private productList(products: FullProductWithAttributes[]) {
        const columns: Property[] = [
            { label: 'Product', src: 'ProductDetails.Name', cls: 'name', callback: this.renderProduct },
            { label: 'SKU', src: 'ProductDetails.ItemId', cls: 'sku' },
            { label: 'Price', src: 'ProductDetails.AdjustedPrice', cls: 'price', callback: this.renderPrice },
            { label: 'Quantity', src: 'ProductDetails.RecordId', cls: 'quantity', callback: this.renderQuantity }
        ];

        return (
            <div className={cls('table')}>

                {/* Table header row */}
                <div className={cls('table-headers')}>

                    {/* Defined Columns */}
                    {columns.map(column => (
                        <React.Fragment key={column.label}>
                            {this.renderTableCell(column.label, cls('table-header'), column.cls)}
                        </React.Fragment>
                    ))}
                </div>

                {/* Table rows */}
                {products?.map(product => {
                    return (
                        <div key={product.ProductDetails.RecordId} className={cls('table-row')}>

                            {/* Defined Columns */}
                            {columns.map(column => {
                                const value = this.getValue(product, column.src);
                                return (
                                    <React.Fragment key={column.label}>
                                        {this.renderTableCell(
                                            column.callback ? column.callback(value, product, this.props) : value,
                                            cls('table-cell'),
                                            column.cls
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    );
                })}

            </div>
        );
    }

    //------------------------------------------------------
    // Render table cell
    //------------------------------------------------------
    private renderTableCell(value?: any, rowClassName?: string, columnClassName?: string): JSX.Element {
        return (
            <div className={classnames(rowClassName, columnClassName)}>
                {value}
            </div>
        );
    }

    //------------------------------------------------------
    //------------------------------------------------------
    private renderProduct = (productName: string, product: FullProductWithAttributes): JSX.Element => {
        const { config, context } = this.props;

        // Get product fallback image
        const emptyPlaceHolderImage = context.app.config.placeholderImageName as string;
        const fallbackImageUrl = getFallbackImageUrl(product.ProductDetails.ItemId, context.request.apiSettings, context.request.app?.config?.OmniChannelMedia) ?? '';
        let fallbackImageSource = fallbackImageUrl;
        if (emptyPlaceHolderImage && fallbackImageUrl) {
            fallbackImageSource = `${fallbackImageUrl},${emptyPlaceHolderImage}`;
        }

        return (
            <a
                className={cls('product')}
                href={`/${String(product.ProductDetails.RecordId)}.p`}
                aria-label={productName}
            >
                <Image
                    className={cls('product-image')}
                    src={product.ProductDetails.PrimaryImageUrl}
                    altText={productName}
                    requestContext={context.actionContext.requestContext}
                    imageSettings={config.productImageSettings}
                    gridSettings={context.request.gridSettings!}
                    loadFailureBehavior='empty'
                    fallBackSrc={fallbackImageSource}
                />
                <span className={cls('product-name')}>
                    {productName}
                </span>
            </a>
        );
    };

    //------------------------------------------------------
    //------------------------------------------------------
    private renderPrice = (price: string): string => {
        return this.props.context.cultureFormatter.formatCurrency(Number(price), 'USD');
    };

    //------------------------------------------------------
    //------------------------------------------------------
    private renderQuantity = (recId: number): JSX.Element => {
        return (
            <input
                type='text'
                min={0}
                className={classnames(cls('input'), 'quantity')}
                pattern='[0-9]*'
                aria-live='polite'
                aria-label='quantity input'
                role='spinbutton'
                aria-valuemin={0}
                onChange={ev => { this.quantitySet(recId, ev.currentTarget.value, ev.currentTarget); }}
                value={this.order[recId] || ''}
            />
        );
    };

    //==========================================================================
    // PRIVATE ACTION METHODS
    //==========================================================================
    //------------------------------------------------------
    //------------------------------------------------------
    private changesSaved = (e: BeforeUnloadEvent) => {
        e.preventDefault();
        e.returnValue = true;
    };

    //------------------------------------------------------
    //------------------------------------------------------
    private getUniqueItems = () => {
        return Object.keys(this.order).length;
    };

    //------------------------------------------------------
    //------------------------------------------------------
    private getTotalItems = () => {
        return Object.values(this.order).reduce((total, cur) => total + cur, 0);
    };

    //------------------------------------------------------
    //------------------------------------------------------
    private getValue(product: FullProductWithAttributes, source: string) {
        const path = source.split('.');
        let value: any = product;

        while (path.length) {
            const fragment = path.shift();
            value = value[fragment!];
        }

        return value;
    }

    //------------------------------------------------------
    //------------------------------------------------------
    @action
    private toggleModal() {
        this.isModalOpen = !this.isModalOpen;
        this.order = {};
    }

    //------------------------------------------------------
    //------------------------------------------------------
    @action
    private quantitySet(recId: number, qtyString: string, element: HTMLInputElement) {
        this.addToCartError = '';
        this.enableAddToCart = false;

        // This is awkward, but changing this.order in place doesn't work.
        const newOrder = { ...this.order };

        let quantity = Math.floor(+qtyString);
        if (isNaN(quantity) || quantity < 0) {
            quantity = 0;
        }

        if (quantity) {
            newOrder[recId] = quantity;
        } else {
            delete newOrder[recId];
        }

        // Clean up the input box (remove zeroes, fractions)
        element.value = `${quantity || ''}`;

        this.order = newOrder;
        this.enableAddToCart = true;

        // If order is not empty, set changes to true to enable warning when exiting page
        if (this.getUniqueItems()) {
            this.hasChanges = true;
        } else {
            this.hasChanges = false;
        }
    }

    //------------------------------------------------------
    //------------------------------------------------------
    @action
    private async addToCart() {
        this.addToCartError = '';
        this.enableAddToCart = false;
        const cartData = this.props.data.cart.result;
        const cartRequest: ProductAddRequest[] = [];

        // eslint-disable-next-line guard-for-in
        for (const recId in this.order) {
            cartRequest.push({
                recId: +recId,
                quantity: this.order[recId],
            });
        }

        try {
            // Add items to the cart
            await addCartLinesAsync({ callerContext: this.props.context.actionContext}, cartData?.cart.Id || '', cartRequest.map(entry => ({
                CatalogId: getCatalogId(this.props.context.request),
                EntryMethodTypeValue: 3,
                ProductId: entry.recId,
                Quantity: entry.quantity,
            })));
            this.isModalOpen = true;
            this.hasChanges = false;
            this.props.data.cart.result?.refreshCart({});
        } catch (error) {
            this.addToCartError = this.props.resources.BulkOrder_addToCartError;
        }
    }
}

export default BulkOrder;
